/*eslint-disable*/
import React, { useEffect, useState } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NoRecordsFound from '../NoRecordsFound';
import {
  hoverRowStyles,
  commonTableCellStyle,
  commonTableRowCellStyle,
} from './tableStyles';
import Pagination from '../Pagination/Pagination';
import DeleteModal from '../modal/deleteModal';
import { menuItemStyles, menuStyles } from '../../styles/menu';
import { iconStyles } from '../../styles/iconStyles';
import { StatusButton } from '../../styles/StatusButton';

interface CommonTableProps {
  data: any[];
  columns: Array<{
    field: string;
    headerName: string;
    align?: 'left' | 'right';
  }>;
  styles: any;
  pages: string;
  statusColumn?: any;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  copyMetadataURL?: (id: string) => void;
  viewMetadata?: (id: string) => void;
  testConnection?: (id: string) => void;
  onSelectedRowsChange?: (selectedRows: string[]) => void;
}

const checkboxStyles = {
  '& .MuiSvgIcon-root': {
    fontSize: {
      xxl: '1.25rem',
      xl: '1.125rem',
      lg: '0.875rem',
    },
  },
};

const CommonTable = ({
  data,
  columns,
  styles,
  pages,
  onEdit,
  onDelete,
  statusColumn = {
    field: 'status',
    header: 'Status',
  },
  copyMetadataURL,
  viewMetadata,
  onSelectedRowsChange,
  testConnection,
}: CommonTableProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<string>('');
  const rowsPerPage = 9;
  const [currentRowId, setCurrentRowId] = useState<string | number | null>(
    null,
  );
  const [page, setPage] = useState(0);
  const [selectAll, setSelectAll] = useState(false);

  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    id: string | number,
  ) => {
    setAnchorEl(event.currentTarget);
    setCurrentRowId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentRowId(null);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const pageCount = Math.ceil(data.length / rowsPerPage);
  const isEmptyRecords = data.length === 0;

  const handleOpenDialog = (id: string) => {
    setRowToDelete(id);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setRowToDelete('');
  };
  const handleConfirmRevoke = () => {
    if (rowToDelete && onDelete) {
      onDelete(rowToDelete);
    }
    handleCloseDialog();
  };

  useEffect(() => {
    const newPageCount = Math.ceil(data.length / rowsPerPage);

    if (page >= newPageCount) {
      const newPage = Math.max(0, newPageCount - 1);
      setPage(newPage);
    }
  }, [data.length, page, rowsPerPage]);

  const handleRowSelection = (id: string) => {
    setSelectedRows((prev: string[]) => {
      let updatedSelection: string[] = [...prev];

      if (updatedSelection.includes(id)) {
        updatedSelection = updatedSelection.filter((rowId) => rowId !== id);
      } else {
        updatedSelection.push(id);
      }

      if (onSelectedRowsChange) {
        onSelectedRowsChange(updatedSelection);
      }
      return updatedSelection;
    });
  };

  const handleSelectAllToggle = () => {
    const currentPageData = data.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
    const currentPageIds = currentPageData.map(
      (row) => row._id || row.id || row.session_key,
    );

    if (selectAll) {
      const updatedSelection = selectedRows.filter(
        (id) => !currentPageIds.includes(id),
      );
      setSelectedRows(updatedSelection);
      if (onSelectedRowsChange) {
        onSelectedRowsChange(updatedSelection);
      }
    } else {
      const updatedSelection = Array.from(
        new Set([...selectedRows, ...currentPageIds]),
      );
      setSelectedRows(updatedSelection);
      if (onSelectedRowsChange) {
        onSelectedRowsChange(updatedSelection);
      }
    }

    setSelectAll(!selectAll);
  };

  const actionsMapping = {
    Authentication: [
      { label: 'Metadata URL', action: copyMetadataURL },
      { label: 'View SP Metadata', action: viewMetadata },
      { label: 'Edit', action: onEdit },
      { label: 'Delete', action: handleOpenDialog },
      { label: 'Test Connection', action: testConnection },
    ],
    Default: [
      { label: 'Edit', action: onEdit },
      { label: 'Delete', action: handleOpenDialog },
    ],
  };
  let actions: {
    label: string;
    action?: (() => void) | ((id: string) => void);
  }[] = [];

  if (pages === 'Authentication') {
    actions = actionsMapping.Authentication;
  } else {
    actions = actionsMapping.Default;
  }

  const isMenuDisabled = (id: string) => {
    const policy = data?.find((p: any) => p._id === id || p.id === id);
    return policy?.identifier === 'default' && pages !== 'Authentication';
  };

  const isMultiSelect = ['Session Management', 'Authentication'].includes(
    pages,
  );
  const hasMenu = ['Policy', 'Authentication', 'Group'].includes(pages);

  return (
    <Box display="flex" flexDirection="column">
      <TableContainer
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          scrollBehavior: 'smooth',
          height: styles.height,
          borderBottom: '0.125rem solid lightgray',
        }}
      >
        <Table sx={{ borderCollapse: 'collapse' }}>
          <TableHead
            sx={{
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
              zIndex: 1,
              boxShadow: '0px 1px 0px lightgray',
            }}
          >
            <TableRow>
              {isMultiSelect && (
                <TableCell
                  key="select"
                  sx={commonTableCellStyle}
                  align="center"
                >
                  <Checkbox
                    checked={
                      data.length > 0 &&
                      data
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage,
                        )
                        .every((row) =>
                          selectedRows.includes(
                            row._id || row.id || row.session_key,
                          ),
                        )
                    }
                    onChange={handleSelectAllToggle}
                    sx={checkboxStyles}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={column.field}
                  sx={commonTableCellStyle}
                  align={column.align || 'left'}
                >
                  {column.headerName}
                </TableCell>
              ))}
              {hasMenu && (
                <TableCell align="right" sx={commonTableCellStyle}>
                  Action
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {isEmptyRecords && (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 1}
                  align="center"
                  sx={{ borderBottom: 'none' }}
                >
                  <NoRecordsFound />
                </TableCell>
              </TableRow>
            )}
            {!isEmptyRecords &&
              data
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row: any) => (
                  <TableRow key={row.id || row._id} sx={{ ...hoverRowStyles }}>
                    {isMultiSelect && (
                      <TableCell sx={commonTableRowCellStyle} align="center">
                        <Checkbox
                          sx={checkboxStyles}
                          checked={selectedRows.includes(
                            row._id || row.session_key,
                          )}
                          onChange={() =>
                            handleRowSelection(row._id || row.session_key)
                          }
                        />
                      </TableCell>
                    )}

                    {columns.map((column) => (
                      <TableCell
                        key={column.field}
                        sx={commonTableRowCellStyle}
                      >
                        {column.headerName === statusColumn?.header ? (
                          <Box>
                            <StatusButton status={row[statusColumn.field]} />
                          </Box>
                        ) : (
                          row[column.field]
                        )}
                      </TableCell>
                    ))}
                    {hasMenu && (
                      <TableCell align="right" sx={commonTableRowCellStyle}>
                        <IconButton
                          onClick={(event) =>
                            handleMenuOpen(event, row.id || row._id)
                          }
                        >
                          <MoreVertIcon sx={iconStyles} />
                        </IconButton>
                        <Menu
                          anchorEl={anchorEl}
                          open={
                            Boolean(anchorEl) &&
                            currentRowId === (row.id || row._id)
                          }
                          onClose={handleMenuClose}
                          sx={menuStyles}
                        >
                          {actions.map(
                            ({ label, action }) =>
                              action && (
                                <MenuItem
                                  key={label}
                                  sx={menuItemStyles}
                                  onClick={() => {
                                    handleMenuClose();
                                    action(row.id || row._id);
                                  }}
                                  disabled={
                                    pages === 'Group'
                                      ? isMenuDisabled(row.id || row._id)
                                        ? (label === 'Delete' && true) ||
                                          (label === 'Edit' && false)
                                        : false
                                      : isMenuDisabled(row.id || row._id)
                                  }
                                >
                                  {label}
                                </MenuItem>
                              ),
                          )}
                        </Menu>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box>
        {!isEmptyRecords && (
          <Box>
            <Pagination
              pageCount={pageCount}
              page={page}
              onPageChange={handleChangePage}
              data={data}
            />
          </Box>
        )}
      </Box>
      <DeleteModal
        open={openDialog}
        title={`Confirm ${pages || 'Item'} Deletion`}
        message={`Are you sure you want to delete the selected user  ${
          pages || 'Item'
        }?`}
        onConfirm={handleConfirmRevoke}
        onCancel={handleCloseDialog}
      />
    </Box>
  );
};

export default CommonTable;
